import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Camera, ArrowRight, ArrowLeft, ArrowUp, ArrowDown,
  ZoomIn, ZoomOut, Move, RotateCw, RotateCcw, Compass, Orbit,
  Video, Film, Rewind, FastForward, Wind, Focus, Aperture,
  ChevronsUp, ChevronsDown, RefreshCw, Cctv, Spline, Eye, Waves,
  MousePointerClick, Sparkles, Airplay, Radio, Layers, Rocket, X,
} from "lucide-react";

/**
 * 42 filmmaker-grade camera moves. Selecting one appends the concrete
 * instruction to the current prompt so the model gets a precise camera cue.
 */
export const CAMERA_PRESETS = [
  { id: "push-in",       name: "Push In",        icon: ArrowRight,  instruction: "slow push-in dolly toward the subject, tightening the frame" },
  { id: "pull-out",      name: "Pull Out",       icon: ArrowLeft,   instruction: "smooth pull-out dolly revealing the wider environment" },
  { id: "pan-left",      name: "Pan Left",       icon: ArrowLeft,   instruction: "steady horizontal pan from right to left" },
  { id: "pan-right",     name: "Pan Right",      icon: ArrowRight,  instruction: "steady horizontal pan from left to right" },
  { id: "tilt-up",       name: "Tilt Up",        icon: ArrowUp,     instruction: "vertical tilt from the ground upward toward the sky" },
  { id: "tilt-down",     name: "Tilt Down",      icon: ArrowDown,   instruction: "vertical tilt from the sky downward toward the ground" },
  { id: "zoom-in",       name: "Zoom In",        icon: ZoomIn,      instruction: "cinematic zoom in on the primary subject" },
  { id: "zoom-out",      name: "Zoom Out",       icon: ZoomOut,     instruction: "cinematic zoom out revealing the full scene" },
  { id: "dolly-shot",    name: "Dolly Shot",     icon: Move,        instruction: "smooth dolly move parallel to the subject" },
  { id: "tracking-shot", name: "Tracking Shot",  icon: Move,        instruction: "tracking shot following the subject's motion" },
  { id: "crane-up",      name: "Crane Up",       icon: ChevronsUp,  instruction: "sweeping crane rise from low to high vantage point" },
  { id: "crane-down",    name: "Crane Down",     icon: ChevronsDown,instruction: "sweeping crane descent from high to low vantage point" },
  { id: "arc-left",      name: "Arc Left",       icon: RotateCcw,   instruction: "arcing camera move around the subject to the left" },
  { id: "arc-right",     name: "Arc Right",      icon: RotateCw,    instruction: "arcing camera move around the subject to the right" },
  { id: "whip-pan",      name: "Whip Pan",       icon: FastForward, instruction: "rapid whip pan with motion blur transitioning across the scene" },
  { id: "roll-shot",     name: "Roll Shot",      icon: RefreshCw,   instruction: "camera rolling on its Z-axis, horizon rotating gently" },
  { id: "dutch-angle",   name: "Dutch Angle",    icon: Spline,      instruction: "tilted Dutch angle with a canted horizon for uneasy tension" },
  { id: "overhead-shot", name: "Overhead Shot",  icon: ChevronsDown,instruction: "top-down overhead god's-eye shot looking straight down" },
  { id: "low-angle",     name: "Low Angle",      icon: ArrowUp,     instruction: "low-angle hero shot looking up at the subject" },
  { id: "high-angle",    name: "High Angle",     icon: ArrowDown,   instruction: "high-angle shot looking down at the subject" },
  { id: "handheld",      name: "Handheld",       icon: Video,       instruction: "raw handheld camera with organic micro-shake and human breath" },
  { id: "steadicam",     name: "Steadicam",      icon: Film,        instruction: "gliding Steadicam follow with buttery-smooth motion" },
  { id: "orbit-left",    name: "Orbit Left",     icon: Orbit,       instruction: "full 180° orbit around the subject counter-clockwise" },
  { id: "orbit-right",   name: "Orbit Right",    icon: Orbit,       instruction: "full 180° orbit around the subject clockwise" },
  { id: "rack-focus",    name: "Rack Focus",     icon: Focus,       instruction: "rack focus shifting sharpness from foreground to background" },
  { id: "follow-shot",   name: "Follow Shot",    icon: MousePointerClick, instruction: "follow shot trailing directly behind the subject" },
  { id: "reveal-shot",   name: "Reveal Shot",    icon: Eye,         instruction: "reveal shot slowly uncovering the hidden subject from behind an obstacle" },
  { id: "fly-through",   name: "Fly Through",    icon: Rocket,      instruction: "immersive fly-through camera weaving through the environment" },
  { id: "dive-down",     name: "Dive Down",      icon: ArrowDown,   instruction: "dramatic dive-down from sky to ground level" },
  { id: "rise-up",       name: "Rise Up",        icon: ArrowUp,     instruction: "graceful rise-up from ground level toward the sky" },
  { id: "spiral-in",     name: "Spiral In",      icon: RotateCw,    instruction: "spiraling camera coiling inward toward the subject" },
  { id: "spiral-out",    name: "Spiral Out",     icon: RotateCcw,   instruction: "spiraling camera unwinding outward away from the subject" },
  { id: "parallax",      name: "Parallax",       icon: Layers,      instruction: "parallax slide showing foreground and background moving at different speeds" },
  { id: "spin-360",      name: "360 Spin",       icon: RefreshCw,   instruction: "complete 360° rotation around the subject" },
  { id: "reverse-dolly", name: "Reverse Dolly",  icon: Rewind,      instruction: "reverse dolly pulling backward while the subject advances toward camera" },
  { id: "crash-zoom",    name: "Crash Zoom",     icon: Aperture,    instruction: "aggressive crash zoom snapping onto the subject" },
  { id: "snorricam",     name: "Snorricam",      icon: Cctv,        instruction: "Snorricam rig locked to the subject's body, world moving around them" },
  { id: "hyperlapse",    name: "Hyperlapse",     icon: FastForward, instruction: "hyperlapse motion time-warping through space at high speed" },
  { id: "timelapse",     name: "Timelapse",      icon: Wind,        instruction: "timelapse pass with clouds and light streaking across time" },
  { id: "vertigo",       name: "Vertigo Effect", icon: Radio,       instruction: "Vertigo dolly-zoom with reverse zoom while dollying in for warped depth" },
  { id: "birds-eye",     name: "Bird's Eye",     icon: Airplay,     instruction: "bird's-eye aerial view soaring high above the scene" },
  { id: "worms-eye",     name: "Worm's Eye",     icon: Compass,     instruction: "worm's-eye ground-level view looking straight up" },
];

/**
 * CameraPresetsMenu — glassmorphic dark popover with a search bar and a
 * scrollable 2-column grid of 42 camera moves. Selecting one calls onSelect
 * with the preset object; the parent appends its `instruction` to the prompt.
 */
export function CameraPresetsMenu({ open, onClose, onSelect, activeId = null }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      // slight delay so the popover mounts before we focus
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CAMERA_PRESETS;
    return CAMERA_PRESETS.filter(
      (p) => p.name.toLowerCase().includes(needle) || p.instruction.toLowerCase().includes(needle)
    );
  }, [q]);

  if (!open) return null;

  return (
    <div className="mb-2 relative" data-testid="camera-presets-menu-wrap">
      <div
        className="absolute left-2 bottom-0 w-[420px] rounded-2xl border border-[#F2C94C]/25 bg-gradient-to-br from-[#0e0b13]/97 via-[#0a0812]/97 to-[#080810]/97 backdrop-blur-xl shadow-[0_0_48px_-12px_rgba(242,201,76,0.45)] overflow-hidden"
        data-testid="camera-presets-menu"
      >
        {/* Aurora rim */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(120% 60% at 0% 0%, rgba(242,201,76,0.10), transparent 55%), radial-gradient(120% 60% at 100% 100%, rgba(0,229,255,0.06), transparent 55%)",
          }}
        />

        {/* Header */}
        <div className="relative px-4 py-2.5 border-b border-[#F2C94C]/15 flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em]">
            <Camera className="w-3 h-3" /> CAMERA PRESETS · {CAMERA_PRESETS.length}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="camera-presets-close"
            className="w-6 h-6 rounded-full border border-white/[0.06] hover:border-[#EB5757]/60 hover:text-[#EB5757] text-white flex items-center justify-center transition-all"
            aria-label="Close camera presets"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Search */}
        <div className="relative px-3 pt-3 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-white absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${CAMERA_PRESETS.length} camera moves…`}
              data-testid="camera-presets-search"
              className="w-full pl-8 pr-3 py-2 rounded-full bg-[#0A0A0A]/70 border border-white/[0.06] focus:border-[#F2C94C]/50 focus:outline-none font-body text-[12px] text-white placeholder:text-white transition-colors"
            />
          </div>
        </div>

        {/* Grid */}
        <div
          className="relative px-3 pb-3 max-h-[46vh] overflow-y-auto"
          data-testid="camera-presets-grid"
        >
          {filtered.length === 0 ? (
            <div className="py-8 flex flex-col items-center gap-2 text-center">
              <Sparkles className="w-4 h-4 text-[#F2C94C]/50" />
              <div className="font-mono-hy text-[10px] text-white tracking-widest">
                NO MOVES MATCH "{q}"
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filtered.map((p) => {
                const Icon = p.icon;
                const active = activeId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onSelect?.(p); onClose?.(); }}
                    data-testid={`camera-preset-${p.id}`}
                    title={p.instruction}
                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-xl border transition-all text-left ${
                      active
                        ? "border-[#F2C94C] bg-[#F2C94C]/10 shadow-[0_0_16px_-6px_rgba(242,201,76,0.6)]"
                        : "border-white/[0.06] bg-white/[0.02] hover:border-[#F2C94C]/50 hover:bg-[#F2C94C]/[0.04] hover:shadow-[0_0_16px_-8px_rgba(242,201,76,0.4)]"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                        active
                          ? "border-[#F2C94C] bg-[#F2C94C]/10"
                          : "border-white/[0.06] bg-[#0A0A0A]/60 group-hover:border-[#F2C94C]/40"
                      }`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${active ? "text-[#F2C94C]" : "text-[#F2C94C]/70 group-hover:text-[#F2C94C]"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-mono-hy text-[10px] tracking-[0.12em] ${active ? "text-[#F2C94C]" : "text-white"} truncate`}>
                        {p.name}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="relative px-4 py-2 border-t border-[#F2C94C]/10 bg-[#0A0A0A]/40">
          <div className="font-mono-hy text-[8px] text-white tracking-[0.22em] leading-relaxed">
            TAP A MOVE → APPENDED TO YOUR PROMPT AS A CAMERA INSTRUCTION
          </div>
        </div>
      </div>
    </div>
  );
}

export default CameraPresetsMenu;
